import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
    Code2, 
    Smartphone, 
    Monitor, 
    Ship, 
    ArrowRight,
    CheckCircle2
} from 'lucide-react';

export const ServicesPage = () => {
    const services = [
        {
            icon: Code2,
            title: 'Custom Software Development',
            description: 'Enterprise software, SaaS applications, and web platforms designed for scalability and efficiency.',
            features: [
                'Enterprise software solutions',
                'SaaS application development',
                'Web platform development',
                'API development & integration',
                'Database design & optimization',
            ],
        },
        {
            icon: Smartphone,
            title: 'Mobile & Web App Development',
            description: 'iOS, Android, and web applications with modern UX/UI, tailored to your business needs.',
            features: [
                'iOS app development',
                'Android app development',
                'Cross-platform solutions',
                'Progressive web apps (PWA)',
                'Modern UX/UI design',
            ],
        },
        {
            icon: Monitor,
            title: 'IT Consulting & Design Services',
            description: 'System integration, design consulting, and process optimization for businesses of all sizes.',
            features: [
                'IT strategy consulting',
                'System integration',
                'Process optimization',
                'UI/UX design services',
                'Brand identity design',
            ],
        },
        {
            icon: Ship,
            title: 'Export & Import Solutions',
            description: 'Global product sourcing, quality assurance, and shipping logistics to deliver worldwide.',
            features: [
                'Global product sourcing',
                'Quality assurance & inspection',
                'Shipping logistics management',
                'Customs documentation',
                'International trade consulting',
            ],
        },
    ];

    return (
        <main className="pt-20" data-testid="services-page">
            {/* Hero Section */}
            <section className="py-16 lg:py-24 bg-[#3C3B6E]" data-testid="services-hero">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <p className="text-[#B22234] font-medium text-sm uppercase tracking-[0.2em] mb-3">
                        Our Services
                    </p>
                    <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tight leading-tight mb-6">
                        Comprehensive Solutions for Your Business
                    </h1>
                    <p className="text-lg text-white/80 max-w-2xl mx-auto">
                        From custom software development to global export solutions, we provide end-to-end services to help your business thrive.
                    </p>
                </div>
            </section>

            {/* Services Grid */}
            <section className="py-16 lg:py-24 bg-white" data-testid="services-list">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
                        {services.map((service, index) => (
                            <Card 
                                key={index}
                                className="bg-gray-50 border border-gray-200 hover:border-[#B22234] transition-all hover:-translate-y-1"
                                data-testid={`service-detail-${index}`}
                            >
                                <CardContent className="p-8">
                                    <div className="w-14 h-14 flex items-center justify-center bg-[#3C3B6E] rounded-lg mb-6">
                                        <service.icon className="w-7 h-7 text-white" />
                                    </div>
                                    <h2 className="text-xl font-black text-[#3C3B6E] tracking-tight mb-3">
                                        {service.title}
                                    </h2>
                                    <p className="text-gray-600 leading-relaxed mb-6">
                                        {service.description}
                                    </p>
                                    <ul className="space-y-3 mb-6">
                                        {service.features.map((feature, featureIndex) => (
                                            <li 
                                                key={featureIndex}
                                                className="flex items-center gap-3 text-gray-600"
                                            >
                                                <CheckCircle2 className="w-5 h-5 text-[#B22234] flex-shrink-0" />
                                                <span className="text-sm">{feature}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <Link to="/contact">
                                        <Button 
                                            className="bg-[#B22234] hover:bg-[#8B1A1A] text-white rounded-md px-6 font-semibold"
                                            data-testid={`service-cta-${index}`}
                                        >
                                            Get Started
                                            <ArrowRight className="ml-2 w-4 h-4" />
                                        </Button>
                                    </Link>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="bg-[#B22234] py-16 lg:py-24" data-testid="services-cta">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white tracking-tight mb-6">
                        Ready to Get Started?
                    </h2>
                    <p className="text-lg text-white/80 max-w-xl mx-auto mb-8">
                        Contact us today to discuss how we can help transform your business with our comprehensive solutions.
                    </p>
                    <Link to="/contact">
                        <Button 
                            size="lg"
                            className="bg-white text-[#B22234] hover:bg-gray-100 rounded-md px-8 py-6 text-base font-semibold"
                            data-testid="services-contact-button"
                        >
                            Contact Us
                            <ArrowRight className="ml-2 w-5 h-5" />
                        </Button>
                    </Link>
                </div>
            </section>
        </main>
    );
};
